import { Injectable } from '@angular/core';
import { HttpClientService } from '../httpClient/httpclient.service';
import { Observable } from 'rxjs/Observable';
import {
  CustomerResearchDto, CustomerSummaryDto, CustomerBasicInfo,
  CustomerFamilyInfo, CustomerLoyaltyInfo, CustomerHobbyPetInfo,
  CustomerServicesInfo,
  mgCustomer,
  mgCustomerDto,
  mgCustomerCompany,
  mgCustomerCompanyDto
} from '../../models/customer.model';
import { CustomerCardListModel } from '../../models/customer-cards.model';
import * as moment from 'moment';
import {Reason} from '../../models/loyalty-reason.model';
import {publishReplay, refCount} from 'rxjs/operators';
import { ApiAddCardModel } from '../../models/api-add-card.model';
import { type } from 'os';

@Injectable()
export class CustomerInfoService {

  private baseUrlLegacy = 'koncentro-customers';
  private baseUrl = 'koncentro-customers/v2';
  private baseUrlValidation = 'koncentro-api';
  private baseUrlLoyalty = 'koncentro-loyalty';
  private cachedFields: Observable<any>;
  private cachedMgCustomerFields: Observable<any>;
  private cachedMgCustomerCompanyFields: Observable<any>;

  customers: CustomerSummaryDto[];

  constructor (private h: HttpClientService) {
    this.customers = [];
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  parseLoyaltyInfo(data: CustomerLoyaltyInfo): void {
    data.flag_delegate = data.flag_delegate === '1';
    data.flag_fidelity = data.flag_fidelity === '1';
    data.flag_signature = data.flag_signature === '1';
  }

  formatServiceInfo(data: CustomerServicesInfo): void {
    console.log('FORMAT SERVICE INFO', data);
    data.flg_state = data.flg_state ? '1' : '0';
    data.flg_blklst = (data.flg_blklst) ? '1' : '0';
    data.reasonId = (data.reasonId > 0 ) ? data.reasonId : null;
    data.serviceId = (data.serviceId > 0 ) ? data.serviceId : null;
  }

  formatBasicInfo(data: CustomerBasicInfo): void {
    console.log('FORMAT BASIC INFO', data);
    data.flg_newsletter = data.flg_newsletter ? data.flg_newsletter : '0';
    data.flag_register_name = (data.flag_register_name) ? '1' : '0';
    data.flag_general_communications = (data.flag_general_communications) ? '1' : '0';
    data.flag_market_research = (data.flag_market_research) ? '1' : '0';
    data.flag_privacy = (data.flag_privacy) ? '1' : '0';
    data.flag_profiling = (data.flag_profiling) ? '1' : '0';
    data.flag_marketing_activities = (data.flag_marketing_activities) ? '1' : '0';
    data.flag_purchase_communications = (data.flag_purchase_communications) ? '1' : '0';
    data.flag_push_notifications = (data.flag_push_notifications) ? '1' : '0';
    data.group_number = (data.group_number > 0 ) ? data.group_number : null ;    // [P workaround T1646
    data.province_id = (data.province_id > 0 ) ? data.province_id : null;        // [P workaround T2499
    data.country_id = (data.country_id > 0 ) ? data.country_id : null;           // [P workaround T2499
    data.preferred_store_id = (data.preferred_store_id > 0 ) ? data.preferred_store_id : null;
  }

  formatMgCustomer(data: mgCustomer): mgCustomerDto {
    let objMgCustomer = new mgCustomerDto();
    objMgCustomer.customerId = data.customerId;
    objMgCustomer.sourceCode = "001";
    objMgCustomer.preferredStoreId = Number(data.puntoVenditaPref);
    objMgCustomer.cardNo = data.cardNo;
    objMgCustomer.privacyAuth = data.flgAuth1 ? "1" : "0";
    objMgCustomer.privacyMktgActivity = data.flgAuth2 ? "1" : "0";
    objMgCustomer.privacyMktgProfiling = data.flgAuth3 ? "1" : "0";
    objMgCustomer.privacyPurchaseCom = "0";
    objMgCustomer.privacyTerminal = "0";
    objMgCustomer.privacyGenericComm = "0";
    objMgCustomer.privacyMktgResearch = "0";
    objMgCustomer.privacyOptin = data.flgAuthPush ? "1" : "0";
    objMgCustomer.checkAccountExistence = true;
    objMgCustomer.surname = data.cognome;
    objMgCustomer.name = data.nome;
    objMgCustomer.fiscalCode = data.fiscalCode.toUpperCase();
    objMgCustomer.birthDate = moment(new Date(data.birthDate)).format('YYYY-MM-DD');
    objMgCustomer.sex = data.sex;
    objMgCustomer.mobile = data.mobile;
    objMgCustomer.email = data.email;
    objMgCustomer.toponymId = data.toponym === null ? 0 : Number(data.toponym);
    objMgCustomer.address1 = data.address;
    objMgCustomer.addressNumber = data.addressNumber;
    objMgCustomer.city = data.comune;
    objMgCustomer.zipCode = data.zipCode;
    objMgCustomer.provinceId = data.province;
    objMgCustomer.checkFiscalDataExistence = data.checkFiscalDataExistence;
    return objMgCustomer;
  }

  formatMgCustomerCompany(data: mgCustomerCompany): mgCustomerCompanyDto {
    let objMgCustomerCompany = new mgCustomerCompanyDto();
    objMgCustomerCompany.customerId = data.customerId;
    objMgCustomerCompany.sourceCode = "001";
    objMgCustomerCompany.cardNo = data.cardNo;
    objMgCustomerCompany.preferredStoreId = Number(data.puntoVenditaPref);
    objMgCustomerCompany.companyData = [{
      flgType: data.flgType,
      rowNum: 1,
      companyName: data.ragioneSociale,
      fiscalCode: data.partitaIva_codiceFiscale,
      vatCode: data.partitaIva_codiceFiscale,
      email1: data.emailAziendale,
      mobile: data.mobileAziendale
    }];
    objMgCustomerCompany.classificationsId = [Number(data.settore)];
    objMgCustomerCompany.surname = data.cognome;
    objMgCustomerCompany.name = data.nome;
    objMgCustomerCompany.birthDate = moment(new Date(data.birthDate)).format('YYYY-MM-DD');
    objMgCustomerCompany.fiscalCode = data.fiscalCode ? data.fiscalCode.toUpperCase() : null;
    objMgCustomerCompany.toponymId = data.toponym === null ? 0 : Number(data.toponym);
    objMgCustomerCompany.address1 = data.address;
    objMgCustomerCompany.addressNumber = data.addressNumber;
    objMgCustomerCompany.city = data.comune;
    objMgCustomerCompany.zipCode = data.zipCode;
    objMgCustomerCompany.provinceId = data.province;
    objMgCustomerCompany.privacyAuth = data.flgAuth1 ? "1" : "0";
    objMgCustomerCompany.privacyMktgActivity = data.flgAuth2 ? "1" : "0";
    objMgCustomerCompany.privacyMktgProfiling = data.flgAuth3 ? "1" : "0";
    objMgCustomerCompany.privacyPurchaseCom = "0";
    objMgCustomerCompany.privacyTerminal = "0";
    objMgCustomerCompany.privacyGenericComm = "0";
    objMgCustomerCompany.privacyMktgResearch = "0";
    objMgCustomerCompany.privacyOptin = "0";
    objMgCustomerCompany.checkAccountExistence = true;
    objMgCustomerCompany.checkFiscalDataExistence = data.checkFiscalDataExistence;
    return objMgCustomerCompany;
  }

  formatAddCard(vCustomerId: number, customerHostId: number, cardId: number, cardNo: string): ApiAddCardModel {
    let objAddCard = new ApiAddCardModel();
    objAddCard.vCustomerId = vCustomerId;
    objAddCard.customerHostId = customerHostId;
    objAddCard.cardId = cardId;
    objAddCard.cardNo = cardNo;
    return objAddCard;
  }

  formatFamilyInfo(data: CustomerFamilyInfo): void {
    data.first_son_birthday = (data.first_son_birthday && data.first_son_birthday != null) ? moment(data.first_son_birthday).format('YYYY-MM-DD') : null;
    data.second_son_birthday = (data.second_son_birthday && data.second_son_birthday != null) ? moment(data.second_son_birthday).format('YYYY-MM-DD') : null;
    data.third_son_birthday = (data.third_son_birthday && data.third_son_birthday != null) ? moment(data.third_son_birthday).format('YYYY-MM-DD') : null;
    data.fourth_son_birthday = (data.fourth_son_birthday && data.fourth_son_birthday != null) ? moment(data.fourth_son_birthday).format('YYYY-MM-DD') : null;
    data.fifth_son_birthday = (data.fifth_son_birthday && data.fifth_son_birthday != null) ? moment(data.fifth_son_birthday).format('YYYY-MM-DD') : null;
    data.mate_birthday = (data.mate_birthday  && data.mate_birthday  != null) ? moment(data.mate_birthday).format('YYYY-MM-DD') : null;
  }

  formatLoyaltyInfo(data: CustomerLoyaltyInfo): void {
    data.flag_signature = (data.flag_signature) ? '1' : '0';
    data.flag_fidelity = (data.flag_fidelity) ? '1' : '0';
    data.flag_delegate = (data.flag_delegate) ? '1' : '0';
    data.welcome_date = (data.welcome_date && data.welcome_date != null) ? moment(data.welcome_date).format(HttpClientService.DATE_FORMAT) : null;
    data.date_signature = (data.date_signature && data.date_signature != null) ? moment(data.date_signature).format(HttpClientService.DATE_FORMAT) : null;
  }

  formatHobbyPetInfo(data: CustomerHobbyPetInfo): void {
    data.flag_hobby_1 = (data.flag_hobby_1) ? '1' : '0';
    data.flag_hobby_2 = (data.flag_hobby_2) ? '1' : '0';
    data.flag_hobby_3 = (data.flag_hobby_3) ? '1' : '0';
    data.flag_hobby_4 = (data.flag_hobby_4) ? '1' : '0';
    data.flag_hobby_5 = (data.flag_hobby_5) ? '1' : '0';
    data.flag_hobby_6 = (data.flag_hobby_6) ? '1' : '0';
    data.flag_hobby_7 = (data.flag_hobby_7) ? '1' : '0';
    data.flag_hobby_8 = (data.flag_hobby_8) ? '1' : '0';
    data.flag_hobby_9 = (data.flag_hobby_9) ? '1' : '0';
    data.flag_hobby_10 = (data.flag_hobby_10) ? '1' : '0';
    data.flag_hobby_11 = (data.flag_hobby_11) ? '1' : '0';
    data.flag_hobby_12 = (data.flag_hobby_12) ? '1' : '0';
  }
  fromBase64ToArrayBuffer(base64String){
    var raw = window.atob(base64String);
    var rawLength = raw.length;
    var array = new Uint8Array(new ArrayBuffer(rawLength));
    for(var i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  }
  get(customerResearchDto: CustomerResearchDto) {
    this.customers = [];
    return this.h.get('/' + this.baseUrl + '/customers', customerResearchDto)
      .map(res => {
        this.customers = res;
        return res;
      });
  }

  delete(customer_id: number) {
    return this.h.delete('/' + this.baseUrl + '/customers/' + customer_id)
      .map(res => {
        const index = this.customers.findIndex(c => c.customerCode === customer_id);
        this.customers = this.customers.splice(index, 1);
      });
  }

  getBasicInfo(customer_id: string): Observable<CustomerBasicInfo> {
    return this.h.get('/' + this.baseUrl + '/customers/' + customer_id + '/basic')
      .map(res => {
        return res;
      });
  }

  getFamilyInfo(customer_id: string) {
    return this.h.get('/' + this.baseUrl + '/customers/' + customer_id + '/family')
      .map(res => {
        return res;
      });
  }

  getLoyaltyInfo(customer_id: string): Observable<CustomerLoyaltyInfo> {
    return this.h.get('/' + this.baseUrl + '/customers/' + customer_id + '/loyalty')
      .map(res => {
        this.parseLoyaltyInfo(res);
        return res;
      });
  }

  /*getServicesInfo(customer_id: string): Observable<CustomerServicesInfo> {
    return this.h.get('/' + this.baseUrlLegacy + '/customer-service/getServices/' + customer_id)
      .map(res => {
        return res;
      });
  }*/

  getServicesFields() {
    if (!this.cachedFields) {
      this.cachedFields = this.h.get('/koncentro-customers/schema/CustomerServiceDto').pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedFields;
  }

  postService(data: CustomerServicesInfo) {
    this.formatServiceInfo(data);
    return this.h.post('/' + this.baseUrlLegacy + '/customer-service', data);
  }

  putService(newData: CustomerServicesInfo) {
    return this.h.put('/' + this.baseUrlLegacy + '/customer-service/' + newData.customerServiceId, newData)
      .map(res => {
        // this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  putRecoveryService(newData: CustomerServicesInfo) {
    return this.h.put('/' + this.baseUrlLegacy + '/customer-service/recovery/' + newData.customerServiceId, '')
      .map(res => {
        // this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  putRecoveryBlacklistService(newData: CustomerServicesInfo) {
    return this.h.put('/' + this.baseUrlLegacy + '/customer-service/recovery-blacklist/' + newData.customerServiceId, '')
      .map(res => {
        // this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  DeleteService(newData: CustomerServicesInfo) {
    return this.h.delete('/' + this.baseUrlLegacy + '/customer-service/' + newData.customerServiceId)
      .map(res => {
        // this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  putSetBlacklistService(newData: CustomerServicesInfo) {
    return this.h.put('/' + this.baseUrlLegacy + '/customer-service/blacklist/' + newData.customerServiceId, newData)
      .map(res => {
        // this.srvNotify.add({severity: 'info', summary: 'Success', detail: 'Operation success'});
      });
  }

  getAllServices(customer_id: string) {
    return this.h.get(this.baseUrlLegacy + '/customer-service/getServices/' + customer_id);
  }

  getSingleService(custServiceId: string) {
    return this.h.get(this.baseUrlLegacy + '/customer-service/' + custServiceId);
  }


  removeService(custServiceId): Observable<Reason> {
    return this.h.delete(this.baseUrl + '/customer-service/' + custServiceId).map(res => {
      return res;
    });
  }

  getVCustomerByHost(customer_id: string): Observable<CustomerLoyaltyInfo> {
    return this.h.get('/' + this.baseUrl + '/vcustomers/customer_host/' + customer_id )
      .map(res => {
        this.parseLoyaltyInfo(res);
        return res;
      });
  }

  getHobbyPetInfo(customer_id: string) {
    return this.h.get('/' + this.baseUrl + '/customers/' + customer_id + '/hobbypet')
      .map(res => {
        return res;
      });
  }

  getCards(customer_id: string): Observable<CustomerCardListModel[]> {
    return this.h.get('/' + this.baseUrl + '/customers/' + customer_id + '/cards')
      .map(res => {
        return res;
      });
  }

  getCustomer(sourceSystemId: string, customerCode: string): Observable<CustomerBasicInfo> {
    return this.h.get('/' + this.baseUrl + '/customers/getCustomer?customerCode=' + customerCode + '&sourceSystemId=' + sourceSystemId)
      .map(res => {
        return res;
      });
  }

  getCustomerNotExists(sourceSystemId: string, customerCode: string): Observable<string> {
    return this.h.get('/' + this.baseUrl + '/customers/getCustomerNotExists?customerCode=' + customerCode + '&sourceSystemId=' + sourceSystemId)
      .map(res => {
        return res;
      });
  }

  getClassification(customer_id: string) {
    return this.h.get('/' + this.baseUrl + '/customers/' + customer_id + '/classification');
  }

  post(data: CustomerBasicInfo) {
    this.formatBasicInfo(data);
    return this.h.post('/' + this.baseUrl + '/customers', data);
  }

  putBasicInfo(customer_id: string, data: CustomerBasicInfo) {
    data.birthday = (data.birthday && data.birthday != null) ? moment(data.birthday).format('YYYY-MM-DD') : null;
    this.formatBasicInfo(data);
    return this.h.put('/' + this.baseUrl + '/customers/' + customer_id + '/basic', data);
  }


  putFamilyInfo(customer_id: string, data: CustomerFamilyInfo) {
    this.formatFamilyInfo(data);
    return this.h.put('/' + this.baseUrl + '/customers/' + customer_id + '/family', data);
  }

  putLoyaltyInfo(customer_id: string, data: CustomerLoyaltyInfo) {
    this.formatLoyaltyInfo(data);
    return this.h.put('/' + this.baseUrl + '/customers/' + customer_id + '/loyalty', data);
  }

  putHobbyPetInfo(customer_id: string, data: CustomerHobbyPetInfo) {
    this.formatHobbyPetInfo(data);
    return this.h.put('/' + this.baseUrl + '/customers/' + customer_id + '/hobbypet', data);
  }

  setClassification(customer_id: number, classifications: Array<{ classification_detail_id: number }>) {
    return this.h.put('/' + this.baseUrl + '/customers/' + customer_id + '/classification', classifications);
  }

  deleteClassification(customer_id: string, classification_id: number) {
    return this.h.delete('/' + this.baseUrl + '/customers/' + customer_id + '/classification/' + classification_id);
  }

  getCustomerInfoByCardNo(cardNo: string) {
    return this.h.get(`koncentro-api/direct/customer/cardInfo?card_no=${cardNo}`);
  }

  getLinkedCustomers(customerId: string) {
    return this.h.get(`/${this.baseUrl}/customers/${customerId}/getLinkedCustomers`);
  }

  getAvailableParents(name?: string) {
    return this.h.get(`/${this.baseUrl}/customers/getParents`, {name}).map(customers =>
      customers.map(customer => {
        const birthDate = customer.birth_date ? moment(customer.birth_date).format('DD/MM/YYYY') : '';
        return ({label: `${customer.name} ${customer.surname} - ${customer.customerHostId} - ${birthDate}`, value: `${customer.customerId}`});
      })
    );
  }

  linkCustomerToParent(customerId: string, parentId: string) {
    return this.h.put(`/${this.baseUrl}/customers/${customerId}/linkCustomerToParent/${parentId}`, {})
  }

  getMgCustomerFields() {
    if (!this.cachedMgCustomerFields) {
      this.cachedMgCustomerFields = this.h.get(`${this.baseUrlValidation}/schema/mg.MgCustomerDto`).pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedMgCustomerFields;
  }
  getMgCustomerCompanyFields() {
    if (!this.cachedMgCustomerCompanyFields) {
      this.cachedMgCustomerCompanyFields = this.h.get(`${this.baseUrlValidation}/schema/mg.MgCustomerCompanyDto`).pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedMgCustomerCompanyFields;
  }
  createMgCustomer(data: mgCustomer) {
    if(data.cardNo && data.cardNo["value"]){
      data.cardNo = data.cardNo["value"];
    }
    let objDto = new mgCustomerDto();
    objDto = this.formatMgCustomer(data);
    return this.h.post('/' + this.baseUrlValidation + '/customer/createCustomer', objDto);
  }

  updateCardStore(cardId:number, cardNo: string, storeId: number){
    return this.h.put('/' + this.baseUrlLoyalty + '/cards/updateCardStore/' + cardId, { "card_no": cardNo, "store_id": storeId });
  }

  createMgCustomerCompany(data: mgCustomerCompany){
    if(data.cardNo && data.cardNo["value"]){
      data.cardNo = data.cardNo["value"];
    }
    let objDto = new mgCustomerCompanyDto();
    objDto = this.formatMgCustomerCompany(data);
    return this.h.post('/' + this.baseUrlValidation + '/customer/createCustomer', objDto);
  }

  updateMgCustomer(data: mgCustomer) {
    let objDto = new mgCustomerDto();
    objDto = this.formatMgCustomer(data);
    //return this.h.put('/' + this.baseUrlValidation + '/customer/updateCustomer', objDto);
    return this.h.put('/' + this.baseUrlValidation + '/internal/updateCustomer/'+objDto.customerId, objDto);
  }

  updateMgCustomerCompany(data: mgCustomerCompany){
    let objDto = new mgCustomerCompanyDto();
    objDto = this.formatMgCustomerCompany(data);
    return this.h.put('/' + this.baseUrlValidation + '/internal/updateCustomer/'+objDto.customerId, objDto);
  }

  addCard(vCustomerId: number, customerHostId: number, cardId: number, cardNo: string): Observable<any> {
    let card = new ApiAddCardModel();
    card = this.formatAddCard(vCustomerId, customerHostId, cardId, cardNo);
    return this.h.put(this.baseUrlValidation + '/internal/addCard', card);
  }

  /*** Storico Moduli ***/
  chackStorageAvailability(customerHostId: string){
    return this.h.get(this.baseUrlLegacy+ "/storage/check-storage-availability?customer_host_id="+customerHostId);
  }
  uploadMgFile(file: File, customerHostId: string,domain: string) {
    let formData:FormData = new FormData();
    formData.append('file', file, file.name);
    return this.h.upload(this.baseUrlLegacy + '/storage/upload-file?customer_host_id='+customerHostId + ((domain && domain!="") ? ("&domain="+domain) : ""), formData);
  }
  getFiles(customerHostId: string,domain:string){
    return this.h.get(this.baseUrlLegacy+ "/storage/list?customer_host_id="+customerHostId + ((domain && domain!="") ? ("&domain="+domain) : ""));
  }
  getFileByBufferArray(customerHostId: string,fileNameStorage: string,domain:string){
    //questo medoto è rimasto esposto per eventuali utilizzi futuri, ma attualmente non viene utilizzato per i file .pdf perchè poi dà dei problemi con alcuni tipi di pdf (KS-3208).
    return this.h.getFileByByteArrayAdv(this.baseUrlLegacy+ "/storage/download?customer_host_id="+customerHostId+"&stored_file="+fileNameStorage + ((domain && domain!="") ? ("&domain="+domain) : ""));
  }
  getFileByBase64(customerHostId: string,fileNameStorage: string,domain:string){
    //utilizzato per riprendere i documenti .pdf legati ad un customer
    return this.h.get(this.baseUrlLegacy+ "/storage/download-base64?customer_host_id="+customerHostId+"&stored_file="+fileNameStorage + ((domain && domain!="") ? ("&domain="+domain) : "")) .map(res => {
      if(res && res.data){
        return this.fromBase64ToArrayBuffer(res.data);
      }
      else{
        return null
      }

    });
  }
  getCustomerAuthInfo(customerId: number) {
    return this.h.get(`${this.baseUrlLegacy}/customer-auth/${customerId}/customer`);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/customer/customerinfo.service.ts